import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { ButtonUI, Checkbox, InputSearch } from 'sonata-react-components';

import { resolveExportLists } from '../helpers/appSiteListExport.helper';
import { getConflictingRowIds, getStrictExportRows, resolveStrictExportRows } from '../helpers/strictSearches.helper';
import { getSelectedIds, getSelectionKey, mergeColumnSelection, toggleShownSelection } from '../Inventory.helpers';
import { InventoryImportBanner } from './InventoryImportBanner';
import { InventoryListColumn } from './InventoryListColumn';
import { InventoryListDownload } from './InventoryListDownload';

import './inventoryList.scss';

import type {
  AppSiteListExportData,
  AppSiteMarketplaceType,
  ImportStatus,
  InventoryOperation,
  InventoryRow
} from '../../AppAndSiteMarketplace.types';

type Props = {
  /** All the excluded rows of the tab (unfiltered), used by the full export. */
  allExcludedRows: InventoryRow[];
  /** All the included rows of the tab (unfiltered), used by the full export. */
  allIncludedRows: InventoryRow[];
  /** Excluded rows to show (already filtered by the search). */
  excludedRows: InventoryRow[];
  id: string;
  /** Status of the CSV import; the banner is shown while it is in progress. */
  importStatus?: ImportStatus;
  /** Included rows to show (already filtered by the search). */
  includedRows: InventoryRow[];
  onDeleteItem: (operation: InventoryOperation, url: string) => void;
  onDeleteSelection: () => void;
  onSearchChange: (searchText: string) => void;
  onSelectionChange: (selectedList: string[]) => void;
  searchText?: string;
  selectedList: string[];
  type: AppSiteMarketplaceType;
};

const accent = (chunks: React.ReactNode) => <span className="inventoryList__accent">{chunks}</span>;

/** Right panel of the inventory: summary, toolbar (search, select all, delete) and the included/excluded columns. */
export function InventoryList({
  allExcludedRows,
  allIncludedRows,
  excludedRows,
  id,
  importStatus = 'idle',
  includedRows,
  onDeleteItem,
  onDeleteSelection,
  onSearchChange,
  onSelectionChange,
  searchText,
  selectedList,
  type
}: Props) {
  const { formatMessage } = useIntl();
  const conflictIds = useMemo(
    // Exclusions take priority: the warning goes on the included rows that are also excluded
    () => getConflictingRowIds(allIncludedRows, allExcludedRows),
    [allIncludedRows, allExcludedRows]
  );
  const getColumnKeys = (operation: InventoryOperation, rows: InventoryRow[]) =>
    rows.map(row => getSelectionKey(operation, row.id));
  const shownKeys = [...getColumnKeys('include', includedRows), ...getColumnKeys('exclude', excludedRows)];
  const shownCount = shownKeys.length;
  const selectedSet = new Set(selectedList);
  const selectedCount = shownKeys.filter(key => selectedSet.has(key)).length;
  const allSelected = shownCount > 0 && selectedCount === shownCount;
  const isSearching = Boolean(searchText);

  const renderColumn = (operation: InventoryOperation, rows: InventoryRow[]) => (
    <InventoryListColumn
      conflictIds={operation === 'include' ? conflictIds : undefined}
      id={`inventoryList-${operation}-${type}`}
      isSearching={isSearching}
      onDeleteItem={onDeleteItem}
      onSelectionChange={columnSelection =>
        onSelectionChange(
          mergeColumnSelection(
            selectedList,
            getColumnKeys(operation, rows),
            columnSelection.map(rowId => getSelectionKey(operation, rowId))
          )
        )
      }
      operation={operation}
      rows={rows}
      selectedList={getSelectedIds(selectedList, operation)}
      type={type}
    />
  );

  const getExportData = (): AppSiteListExportData => {
    if (type === 'specific') {
      const { exclude, include } = resolveStrictExportRows({
        exclude: getStrictExportRows(allExcludedRows),
        include: getStrictExportRows(allIncludedRows)
      });
      return { exclude: [], excludeRows: exclude, include: [], includeRows: include, type };
    }
    return {
      ...resolveExportLists({
        exclude: allExcludedRows.map(row => row.label),
        include: allIncludedRows.map(row => row.label)
      }),
      type
    };
  };

  return (
    <div className="inventoryList">
      <div className="inventoryList__header">
        <div className="inventoryList__title">
          {type === 'generic'
            ? formatMessage({
                defaultMessage: 'This is your list of included/excluded sites',
                id: 'appAndSiteMarketplace.inventory.list.title.generic'
              })
            : formatMessage({
                defaultMessage: 'This is your list of included/excluded keys',
                id: 'appAndSiteMarketplace.inventory.list.title.specific'
              })}
        </div>
        <div className="inventoryList__summary">
          <FormattedMessage
            defaultMessage="Results: <accent>{count}</accent>"
            id="appAndSiteMarketplace.inventory.list.results"
            values={{ accent, count: shownCount }}
          />
          <FormattedMessage
            defaultMessage="Selected: <accent>{count}</accent>"
            id="appAndSiteMarketplace.inventory.list.selected"
            values={{ accent, count: selectedCount }}
          />
        </div>
      </div>
      <div className="inventoryList__toolbar">
        <div className="inventoryList__search">
          <InputSearch
            id={`searchInput-${id}`}
            lens
            name="search"
            onChange={onSearchChange}
            placeholder={formatMessage({
              defaultMessage: 'Search by name...',
              id: 'appAndSiteMarketplace.inventory.search.placeholder'
            })}
            tiny
            value={searchText}
          />
        </div>
        <div className="inventoryList__toolbarActions">
          <Checkbox
            active={allSelected}
            disabled={shownCount === 0}
            id={`selectAll-${id}`}
            labelText={formatMessage({
              defaultMessage: 'Select all',
              id: 'appAndSiteMarketplace.inventory.list.selectAll'
            })}
            onClickAction={(_: string, checked: boolean) =>
              onSelectionChange(toggleShownSelection(selectedList, shownKeys, checked))
            }
            tiny
          />
          <ButtonUI
            className="inventoryList__toolbarActionsDelete"
            disabled={selectedCount === 0}
            icon="fa-regular fa-trash"
            id={`deleteSelectedAction-${id}`}
            onClick={onDeleteSelection}
            shape="link"
            size="ultraSmall"
            text={formatMessage({
              defaultMessage: 'Delete',
              id: 'appAndSiteMarketplace.inventory.list.delete'
            })}
            type="secondary"
          />
          <InventoryListDownload
            disabled={allIncludedRows.length === 0 && allExcludedRows.length === 0}
            getData={getExportData}
            id={id}
          />
        </div>
      </div>
      <InventoryImportBanner status={importStatus} />
      <div className="inventoryList__columns">
        {renderColumn('include', includedRows)}
        {renderColumn('exclude', excludedRows)}
      </div>
    </div>
  );
}
